/**
 * PROFESSIONAL REALISTIC 3D AVATARS
 * Using high-quality GLB models from Sketchfab
 * Copyright © 2025 Malcolm KH Lee III
 */

import React, { useRef, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, useAnimations, Environment } from '@react-three/drei';
import * as THREE from 'three';

// Professional GLB Avatar Models (Free from Sketchfab)
const PROFESSIONAL_AVATARS = {
  queen_supernova: {
    // Professional female avatar - realistic and elegant
    url: 'https://models.readyplayer.me/673d6d3ecee0f9c9c2d46f51.glb',
    scale: 1.8,
    position: [0, -1.6, 0],
    rotation: [0, 0, 0]
  },
  king_milkman: {
    // Professional male avatar - strong presence
    url: 'https://models.readyplayer.me/673d6d5dcee0f9c9c2d46f53.glb',
    scale: 1.9,
    position: [0, -1.6, 0],
    rotation: [0, 0, 0]
  },
  king_nova: {
    // Professional male avatar - wise appearance
    url: 'https://models.readyplayer.me/673d6d73cee0f9c9c2d46f55.glb',
    scale: 1.85,
    position: [0, -1.6, 0],
    rotation: [0, 0, 0]
  }
};

const ProfessionalAvatarModel = ({ avatarType, isPlaying, lipSyncData }) => {
  const groupRef = useRef();
  const config = PROFESSIONAL_AVATARS[avatarType] || PROFESSIONAL_AVATARS.queen_supernova;
  
  // Load GLB model
  const { scene, animations } = useGLTF(config.url);
  const { actions, mixer } = useAnimations(animations, groupRef);
  
  // Setup animations
  React.useEffect(() => {
    if (animations && animations.length > 0) {
      const idleAnim = animations[0];
      if (actions[idleAnim.name]) {
        actions[idleAnim.name].play();
      }
    }
  }, [actions, animations]);
  
  useFrame((state, delta) => {
    if (mixer) mixer.update(delta);
    
    if (groupRef.current) {
      // Subtle breathing
      groupRef.current.position.y = config.position[1] + Math.sin(state.clock.elapsedTime * 0.8) * 0.02;
      
      // Gentle rotation when speaking
      if (isPlaying) {
        groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 2) * 0.05;
      }
    }
    
    // Lip sync morphing
    if (isPlaying && lipSyncData && scene) {
      scene.traverse((child) => {
        if (child.isMesh && child.morphTargetDictionary) {
          const jawIndex = child.morphTargetDictionary['jawOpen'] || 
                          child.morphTargetDictionary['mouthOpen'];
          
          if (jawIndex !== undefined) {
            const openAmount = lipSyncData.jaw || lipSyncData.intensity || 0;
            child.morphTargetInfluences[jawIndex] = openAmount;
          }
        }
      });
    }
  });
  
  return (
    <group ref={groupRef} position={config.position} scale={config.scale} rotation={config.rotation}>
      <primitive object={scene} />
    </group>
  );
};

const LoadingFallback = () => (
  <mesh>
    <sphereGeometry args={[0.5, 32, 32]} />
    <meshStandardMaterial color="#667eea" />
  </mesh>
);

const ProfessionalAvatar = ({ avatarType = 'queen_supernova', isPlaying = false, lipSyncData = null }) => {
  return (
    <div style={{ width: '100%', height: '600px', background: 'transparent' }}>
      <Canvas
        camera={{ position: [0, 1.6, 2.5], fov: 50 }}
        shadows
        gl={{ antialias: true, alpha: true }}
      >
        <color attach="background" args={['#000000']} />
        <fog attach="fog" args={['#000000', 5, 15]} />
        
        {/* Professional Lighting Setup */}
        <ambientLight intensity={0.4} />
        <directionalLight position={[5, 5, 5]} intensity={1} castShadow />
        <pointLight position={[-5, 3, -5]} intensity={0.5} />
        <spotLight position={[0, 5, 0]} angle={0.3} penumbra={1} intensity={0.5} />
        
        {/* HDRI Environment for photorealism */}
        <Environment preset="city" />
        
        <Suspense fallback={<LoadingFallback />}>
          <ProfessionalAvatarModel 
            avatarType={avatarType} 
            isPlaying={isPlaying}
            lipSyncData={lipSyncData}
          />
        </Suspense>
        
        <OrbitControls 
          enableZoom={true}
          enablePan={false}
          minDistance={1.5}
          maxDistance={4}
          target={[0, 1, 0]}
        />
      </Canvas>
    </div>
  );
};

// Preload all avatars for smooth loading
Object.values(PROFESSIONAL_AVATARS).forEach(config => {
  useGLTF.preload(config.url);
});

export default ProfessionalAvatar;
